import type { ColumnDef } from '@tanstack/react-table';
import styled from 'styled-components';
import { FcCheckmark } from 'react-icons/fc';

import { colors } from 'constants/colors';
import { DataTable, textColumn } from 'components/DataTable';
import { PaymentMethodType } from './types';
import { capitalize } from 'pages/Users/utils';
import { PaymentMethodPaymentType, PaymentType } from 'data/__generated__';

function getProvider({ paymentType, provider, bank }: { paymentType: string; provider: string; bank?: string | null }) {
    if (paymentType === PaymentType.Cb) {
        return `CB - ${capitalize(provider)}`;
    }
    if (paymentType === PaymentMethodPaymentType.Swile) {
        return 'Swile';
    }
    if (paymentType === PaymentMethodPaymentType.Edenred) {
        return 'TR - Edenred';
    }
    if (paymentType === PaymentMethodPaymentType.Restoflash) {
        return 'Restoflash - Paygreen';
    }
    if (paymentType === PaymentMethodPaymentType.Trd) {
        if (bank?.match('Natixis')) {
            return 'Apetiz - Paygreen';
        }
        if (bank?.match('UP')) {
            return 'UP - Paygreen';
        }
        if (bank?.match('Sodexo')) {
            return 'Sodexo - Paygreen';
        }
        return 'Inconnu - Paygreen';
    }
    return `autre (${paymentType})`;
}

const columns: ColumnDef<PaymentMethodType, unknown>[] = [
    textColumn<PaymentMethodType, Record<string, unknown>>({
        id: 'provider',
        accessorFn: (row) =>
            getProvider({ paymentType: row.paymentType, provider: row.provider, bank: row.details.bank }),
        header: 'Fournisseur',
        defaultVisible: true,
    }),
    textColumn<PaymentMethodType, Record<string, unknown>>({
        id: 'identifier',
        accessorFn: (row) => {
            const { number, username } = row.details;
            const cardDigits = (number?.length && '**' + number.substr(number.length - 4)) || '**xxxx';
            return (number && cardDigits) || username || 'inconnu';
        },
        header: 'Numéro / compte',
        defaultVisible: true,
    }),
    textColumn<PaymentMethodType, Record<string, unknown>>({
        id: 'expiration',
        accessorFn: (row) => row.details.expiration || '',
        header: 'Exp.',
        defaultVisible: true,
        cell: ({ row }) => {
            const expiration = row.original.details.expiration;
            const formattedExpiration = expiration ? expiration.slice(0, 2) + '/' + expiration.slice(2) : '';
            return (
                <Expiration>
                    {formattedExpiration}
                    {row.original.expired ? <ExpiredDot /> : null}
                </Expiration>
            );
        },
    }),
    textColumn<PaymentMethodType, Record<string, unknown>>({
        id: 'retries',
        accessorFn: (row) => (row.retries === undefined ? 'NA' : row.retries.toString()),
        header: 'Echecs',
        defaultVisible: true,
    }),
    textColumn<PaymentMethodType, Record<string, unknown>>({
        id: 'isDefault',
        accessorFn: (row) => (row.default ? 'Oui' : 'Non'),
        header: 'Défaut',
        defaultVisible: true,
        cell: ({ row }) => (row.original.default ? <FcCheckmark /> : null),
    }),
];

export function CardAndMealVoucherPaymentMethods({ paymentMethods }: { paymentMethods: PaymentMethodType[] }) {
    return (
        <DataTable<PaymentMethodType>
            columns={columns}
            data={paymentMethods}
            filterMode="client"
            getRowId={(row) => row._id}
            disableUrlSync
            autoFocusSearch={false}
        />
    );
}

const Expiration = styled.div`
    display: flex;
    align-items: center;
`;

const ExpiredDot = styled.div`
    background-color: ${colors.red};
    width: 10px;
    height: 10px;
    border-radius: ${({ theme }) => theme.borderRadius};
    margin-left: 5px;
`;
